import { useState, useEffect } from "react";
import { perfumeAPI } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Search,
  Edit,
  Trash2,
  Save,
  X,
  List,
  Plus,
} from "lucide-react";

const PerfumeList = () => {
  const [perfumes, setPerfumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ code: "", name: "" });
  const [deleteId, setDeleteId] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newPerfume, setNewPerfume] = useState({ code: "", name: "" });

  useEffect(() => {
    loadPerfumes();
  }, []);

  const loadPerfumes = async () => {
    try {
      const res = await perfumeAPI.getAll();
      setPerfumes(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei profumi");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (perfume) => {
    setEditingId(perfume.id);
    setEditData({ code: perfume.code, name: perfume.name });
  };

  const handleSave = async (id) => {
    try {
      await perfumeAPI.update(id, {
        code: editData.code,
        name: editData.name,
      });
      toast.success("Profumo aggiornato");
      setEditingId(null);
      loadPerfumes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({ code: "", name: "" });
  };

  const handleDelete = async () => {
    try {
      await perfumeAPI.delete(deleteId);
      toast.success("Profumo eliminato");
      setDeleteId(null);
      loadPerfumes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const handleAddNew = async (e) => {
    e.preventDefault();
    try {
      await perfumeAPI.create({
        code: newPerfume.code,
        name: newPerfume.name,
        format_costs: [],
        quantity_ml: 0,
        alert_threshold_ml: 500,
      });
      toast.success("Profumo aggiunto");
      setIsAddOpen(false);
      setNewPerfume({ code: "", name: "" });
      loadPerfumes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiunta");
    }
  };

  const filteredPerfumes = perfumes.filter(
    (p) =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="perfume-list-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-2">Elenco Profumi</h1>
          <p className="text-gray-500">
            {perfumes.length} profumi nel catalogo
          </p>
        </div>
        <Button
          onClick={() => setIsAddOpen(true)}
          data-testid="add-new-perfume-btn"
          className="bg-[#C9A227] text-black hover:bg-[#FFD700]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Profumo
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          data-testid="search-perfume-list"
          placeholder="Cerca per nome o codice..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white border-gray-200"
        />
      </div>

      {/* Results count */}
      {searchQuery && (
        <p className="text-sm text-gray-500">
          {filteredPerfumes.length} risultati trovati
        </p>
      )}

      {/* Table */}
      <Card className="bg-white/60 border-[#C9A227]/15 overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-[#C9A227] animate-pulse">Caricamento...</div>
            </div>
          ) : filteredPerfumes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <List className="w-12 h-12 mb-3 opacity-30" />
              <p>Nessun profumo trovato</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh]">
              <Table>
                <TableHeader className="sticky top-0 bg-white">
                  <TableRow className="border-gray-200 hover:bg-transparent">
                    <TableHead className="text-[#C9A227] w-[100px]">Codice</TableHead>
                    <TableHead className="text-[#C9A227] w-[200px]">Nome</TableHead>
                    <TableHead className="text-[#C9A227]">Note Olfattive</TableHead>
                    <TableHead className="text-[#C9A227] text-right w-[100px]">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPerfumes.map((perfume) => (
                    <TableRow
                      key={perfume.id}
                      data-testid={`perfume-list-row-${perfume.code}`}
                      className="border-gray-200 hover:bg-[#C9A227]/5"
                    >
                      <TableCell>
                        {editingId === perfume.id ? (
                          <Input
                            value={editData.code}
                            onChange={(e) => setEditData({ ...editData, code: e.target.value })}
                            className="h-8 bg-gray-50 border-[#C9A227] w-[80px]"
                            data-testid={`edit-code-${perfume.id}`}
                          />
                        ) : (
                          <span className="font-medium text-[#C9A227]">{perfume.code}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === perfume.id ? (
                          <Input
                            value={editData.name}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                            className="h-8 bg-gray-50 border-[#C9A227]"
                            data-testid={`edit-name-${perfume.id}`}
                          />
                        ) : (
                          <span className="text-gray-700">{perfume.name}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-500 text-sm line-clamp-2">
                          {perfume.notes || <span className="text-gray-300 italic">Nessuna nota</span>}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {editingId === perfume.id ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSave(perfume.id)}
                              className="h-8 w-8 p-0 text-green-400 hover:text-green-300 hover:bg-green-400/10"
                              data-testid={`save-${perfume.id}`}
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancel}
                              className="h-8 w-8 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-400/10"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(perfume)}
                              className="h-8 w-8 p-0 text-gray-500 hover:text-[#C9A227] hover:bg-[#C9A227]/10"
                              data-testid={`edit-btn-${perfume.code}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteId(perfume.id)}
                              className="h-8 w-8 p-0 text-gray-500 hover:text-red-400 hover:bg-red-400/10"
                              data-testid={`delete-btn-${perfume.code}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add New Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="bg-white border-[#C9A227]/20">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif text-gray-900">
              Nuovo Profumo
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddNew} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-gray-700">Codice</label>
              <Input
                value={newPerfume.code}
                onChange={(e) => setNewPerfume({ ...newPerfume, code: e.target.value })}
                placeholder="ES001"
                required
                className="bg-gray-50 border-gray-200"
                data-testid="new-perfume-code"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-gray-700">Nome</label>
              <Input
                value={newPerfume.name}
                onChange={(e) => setNewPerfume({ ...newPerfume, name: e.target.value })}
                placeholder="Nome Profumo"
                required
                className="bg-gray-50 border-gray-200"
                data-testid="new-perfume-name"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#C9A227] text-black hover:bg-[#FFD700]"
              data-testid="submit-new-perfume"
            >
              Aggiungi Profumo
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Sei sicuro di voler eliminare questo profumo? Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#1A1A1A] border-gray-200 text-gray-700 hover:bg-[#27272A]">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-gray-900 hover:bg-red-700"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PerfumeList;
